import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import ArrDataGrid from '../../common/arr-data-grid/ArrDataGrid';
import {getProjectFieldListGridColumns} from './ProjectFieldListGridColumn';
import {PAGE_SIZE, ToastType} from '../../../../src/shared/constants';
import {t} from 'i18next';
import './ProjectFieldGrid.scss';
import {Box, Typography} from '@mui/material';
import {ReactComponent as FolderIcon} from '../../../../src/assets/images/no-data-found.svg';
import {ISelectItem} from '../../../../src/shared/types';
import {
  GetProjectFieldListRequestParams,
  ProjectField,
} from '../../../../src/api/endpoints/projectApi';
import {GridRenderCellParams, GridSortModel} from '@mui/x-data-grid';
import ArrButton from '../../../../src/components/common/arr-button/ArrButton';
import {isEqual, sortBy} from 'lodash';
import {
  useProjectFieldList,
  useUpdateProjectActivationState,
} from '../../../hooks/projectField/useProjectFieldViewList';
import ArrTextField from '../../../../src/components/common/arr-text-field/ArrTextField';
import ArrToastNotification from '../../common/arr-toast-notification/ArrToastNotification';
import ArrModal from '../../common/arr-modal/ArrModal';

import {
  AddEditProjectFieldModal,
  AddEditProjectFieldModalRef,
} from '../add-edit-project-field/AddEditProjectFieldModal';
import {
  AddProjectFieldGroupModal,
  AddProjectFieldGroupModalRef,
} from '../add-project-field-group/AddProjectFieldGroupModal';
import {IProjectFieldDetailsResponse} from '../../../api/types/project.type';
interface IprojectListGridProps {
  selectedGroup: ISelectItem | null;
}

const ProjectFieldGrid: React.FC<IprojectListGridProps> = ({
  selectedGroup,
}: IprojectListGridProps) => {
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize] = useState(PAGE_SIZE);
  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: 'projectFieldName',
      sort: 'asc',
    },
  ]);
  const {mutate: updateProjectActivationState} =
    useUpdateProjectActivationState();

  const previousSelectedGroupRef = useRef<{
    selectedGroup: ISelectItem | null;
    sortModel: GridSortModel;
  }>({
    selectedGroup: null,
    sortModel,
  });
  const isOnlyPageChangeRef = useRef<boolean>(false);
  const [filterValue, setFilterValue] = useState('');
  const [filter, setFilter] = useState('');

  const addEditProjectFieldRef = useRef<AddEditProjectFieldModalRef>(null);
  const addEditProjectGrpRef = useRef<AddProjectFieldGroupModalRef>(null);

  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState(ToastType.Success);
  const [showActivationModal, setShowActivationModal] = useState(false);
  const [isReindexing, setIsReindexing] = useState(false);
  const [currentItem, setCurrentItem] = useState<ProjectField | null>(null);
  const isResetPageNumber = useCallback(() => {
    let resetPageNumber = false;
    if (
      previousSelectedGroupRef.current?.selectedGroup?.id !== selectedGroup?.id
    ) {
      previousSelectedGroupRef.current = {
        ...previousSelectedGroupRef.current,
        selectedGroup,
      };
      resetPageNumber = true;
    } else if (
      !isEqual(
        sortBy(previousSelectedGroupRef.current?.sortModel, ['field', 'sort']),
        sortBy(sortModel, ['field', 'sort'])
      )
    ) {
      previousSelectedGroupRef.current = {
        ...previousSelectedGroupRef.current,
        sortModel,
      };
      resetPageNumber = true;
    }
    return resetPageNumber;
  }, [selectedGroup, sortModel]);

  const params = useMemo(() => {
    const getPageNumber = isOnlyPageChangeRef.current ? pageNumber : 1;
    const payload: GetProjectFieldListRequestParams = {
      filters: filterValue,
      SortBy: sortModel[0]?.field
        ? sortModel[0]?.field === 'dataType'
          ? 'DataTypeCode'
          : sortModel[0]?.field
        : 'projectFieldName',
      SortingOrder: sortModel[0]?.sort || 'asc',
      PageNumber: getPageNumber,
      PageSize: pageSize,
    };

    if (isResetPageNumber() && !isOnlyPageChangeRef.current)
      setTimeout(() => setPageNumber(1), 100);

    if (isOnlyPageChangeRef.current) isOnlyPageChangeRef.current = false;
    return payload;
  }, [filterValue, isResetPageNumber, pageNumber, pageSize, sortModel]);

  const {
    data: projectListGridResponse,
    isLoading,
    refetch: RefetchProjectFieldList,
  } = useProjectFieldList(params);

  const projectListGridData = useMemo(
    () => projectListGridResponse?.projectFields ?? [],
    [projectListGridResponse?.projectFields]
  );

  const totalRecords = useMemo(
    () => projectListGridResponse?.totalRecords,
    [projectListGridResponse?.totalRecords]
  );

  const isNoData = useMemo(
    () => projectListGridData?.length <= 0,
    [projectListGridData?.length]
  );

  const handleEdit = useCallback((projectId: string) => {
    if (addEditProjectFieldRef.current) {
      addEditProjectFieldRef.current.open(projectId);
    }
  }, []);

  const handleActivateDeactivate = useCallback((data: GridRenderCellParams) => {
    setIsReindexing(false);
    setCurrentItem(data as unknown as ProjectField);
    setShowActivationModal(true);
  }, []);

  const confirmActivation = useCallback(() => {
    if (currentItem) {
      setShowActivationModal(false);
      updateProjectActivationState(currentItem, {
        onSuccess: () => {
          setShowToast(true);
          setToastType(ToastType.Success);
          RefetchProjectFieldList();
          setToastMessage(
            t('manageProjectFields.modal.successMsg', {
              type: currentItem.isActive ? 'deactivated' : 'activated',
            })
          );
          if (currentItem.isSearchable) {
            setIsReindexing(true);
            setShowActivationModal(true);
          }
        },
        onError: (error: any) => {
          setShowToast(true);
          setToastType(ToastType.Error);
          if (error?.response?.data?.detail) {
            const trimmedMessaged =
              error?.response?.data?.detail.split('\n')[0];
            setToastMessage(trimmedMessaged);
          }
        },
      });
    }
  }, [currentItem]);

  useEffect(() => {
    function updateAriaCurrentPage() {
      const pageBtn = document.querySelector('.page-button.active');

      if (pageBtn) {
        pageBtn.setAttribute('aria-current', 'page');
      }
    }

    const observer = new MutationObserver(() => {
      updateAriaCurrentPage();
    });

    observer.observe(document.body, {
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });

    return () => observer.disconnect();
  }, []);

  const handleApplyFilter = useCallback(() => {
    setFilterValue(filter);
    isOnlyPageChangeRef.current = true;
    setPageNumber(1);
  }, [filter]);

  const handleOnChange = useCallback((event: any) => {
    setFilter(event.target.value);
  }, []);

  const handlePageNumber = useCallback((pageNumber: number) => {
    isOnlyPageChangeRef.current = true;
    setPageNumber(pageNumber);
  }, []);

  const NoRowsOverlay = useCallback(
    () => (
      <Box className="no-grid-overlay">
        <FolderIcon />
        <Typography
          tabIndex={0}
          className="typo-text headline3_size"
          data-testid={`project-field-grid-no-results`}>
          {t('common.arrDataGrid.noResults', {
            entity: t('manageProjectFields.projectFields'),
          })}
        </Typography>
      </Box>
    ),
    []
  );

  const handleKeyEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      handleApplyFilter();
    }
  };

  const addProjectGrpModal = () => {
    if (addEditProjectGrpRef.current) {
      addEditProjectGrpRef.current.open();
    }
  };

  const addProjectFieldModal = () => {
    if (addEditProjectFieldRef.current) {
      addEditProjectFieldRef.current.open();
    }
  };

  const reloadProjectList = useCallback(
    (projectFieldDetail?: IProjectFieldDetailsResponse) => {
      if (projectFieldDetail) {
        RefetchProjectFieldList();
      } else {
        setFilterValue(filter);
        isOnlyPageChangeRef.current = true;
        setPageNumber(1);
      }
    },
    [filter]
  );

  let ariaDescription: string;
  if (isReindexing) {
    ariaDescription = t('manageProjectFields.modal.descriptionReindex');
  } else if (currentItem?.isActive) {
    ariaDescription = t('manageProjectFields.modal.descriptionDeactivate');
  } else {
    ariaDescription = t('manageProjectFields.modal.descriptionActivate');
  }

  let descriptionText: string;
  if (isReindexing) {
    descriptionText = t('manageProjectFields.modal.descriptionReindex');
  } else if (currentItem?.isActive) {
    descriptionText = t('manageProjectFields.modal.descriptionDeactivate');
  } else {
    descriptionText = t('manageProjectFields.modal.descriptionActivate');
  }

  return (
    <div data-testid="project-field-list-grid">
      <Box className={'project-field-list-filter'}>
        <Box className={'subheader-container-apply-filter'}>
          <Typography
            data-testid="filter-label"
            className="typo-text body3_size"
            tabIndex={0}
            aria-label={t('manageProjectFields.filterFieldsBy')}
          >
            {t('manageProjectFields.filterFieldsBy')}
          </Typography>
          <ArrTextField
            state="enabled"
            sizeVariant="medium"
            slotProps={{inputLabel: {shrink: false}}}
            placeholder={t('manageProjectFields.fieldNamePlaceholder')}
            aria-label={t('manageProjectFields.fieldNamePlaceholder')}
            value={filter}
            onChange={handleOnChange}
            onKeyDown={handleKeyEnter}
            dataTestId="project-field-filter-input"
            type="clear"
          />
          <ArrButton
            aria-label={t('manageProjectFields.applyFilter')}
            variant="secondary"
            size="medium"
            onClick={handleApplyFilter}
            dataTestId="apply-filter-btn"
          >
            {t('manageProjectFields.applyFilter')}
          </ArrButton>
        </Box>
        <Box className={'subheader-container'}>
          <ArrButton
            dataTestId="add-project-field"
            aria-label={t('manageProjectFields.addProjectField')}
            variant="primary"
            size="medium"
            onClick={addProjectFieldModal}
          >
            {t('manageProjectFields.addProjectField')}
          </ArrButton>
          <ArrButton
            dataTestId="add-project-field-name"
            aria-label={t('manageProjectFields.addProjectFieldName')}
            variant="primary"
            size="medium"
            onClick={addProjectGrpModal}
          >
            {t('manageProjectFields.addProjectFieldName')}
          </ArrButton>
        </Box>
      </Box>
      <AddEditProjectFieldModal
        ref={addEditProjectFieldRef}
        onSaveSuccess={reloadProjectList}
      />
      <AddProjectFieldGroupModal ref={addEditProjectGrpRef} />
      <ArrDataGrid
        className={
          isNoData
            ? 'comments-list-grid no-grid-overlay-height'
            : 'project-field-list-grid'
        }
        loading={isLoading}
        columns={getProjectFieldListGridColumns({
          handleEdit,
          handleActivateDeactivate,
        })}
        rows={projectListGridData}
        primaryKey="projectFieldId"
        noRowsOverlay={NoRowsOverlay}
        hideFooter={isNoData}
        rowCount={totalRecords}
        currentPage={pageNumber}
        itemsPerPage={PAGE_SIZE}
        showPerPageOptions={true}
        pageSizeOptions={[PAGE_SIZE]}
        paginationMode="server"
        onPageChange={handlePageNumber}
        sortModel={sortModel}
        onSortModelChange={setSortModel}
      />
      <ArrModal
        showModal={showActivationModal}
        ariaLabel={
          isReindexing
            ? t('manageProjectFields.modal.titleReindex')
            : t('manageProjectFields.modal.title')
        }
        title={
          isReindexing
            ? t('manageProjectFields.modal.titleReindex')
            : t('manageProjectFields.modal.title')
        }
        ariaDescription={ariaDescription}
        description={descriptionText}
        autoFocusCloseButton
        onClose={() => setShowActivationModal(false)}
        dataTestId="projectfieldgrid-modal"
        buttons={
          isReindexing
            ? [
                {
                  id: 'ok-button',
                  label: t('common.ok'),
                  dataTestId: 'pfg-ok-button',
                  onClick: () => {
                    setShowActivationModal(false);
                    setTimeout(() => setIsReindexing(false));
                  },
                  variant: 'primary',
                },
              ]
            : [
                {
                  id: 'cancel-button',
                  label: t('common.cancel'),
                  dataTestId: 'pfg-cancel-button',
                  onClick: () => setShowActivationModal(false),
                  variant: 'secondary',
                },
                {
                  id: 'status-button',
                  label: currentItem?.isActive
                    ? t('manageProjectFields.modal.deactivate')
                    : t('manageProjectFields.modal.activate'),
                  dataTestId: 'pfg-status-button',
                  onClick: confirmActivation,
                  variant: 'primary',
                },
              ]
        }
      />
      {showToast && (
        <ArrToastNotification
          message={toastMessage}
          onClose={() => setShowToast(false)}
          status={[toastType]}
          setErrorIconAsAlert
          closeButtonAriaLabel={t(
            'manageProjectField.dismissNotificationAriaLable'
          )}
        />
      )}
    </div>
  );
};

export {ProjectFieldGrid};
